import React, { useState } from 'react';
import { connect } from 'react-redux';
import {
  ArrowRight,
  ArrowRight2,
  Button,
  cn,
  Text,
  useIsInIframe,
  useResponsiveBreakpoints,
  useWasOpenedByWindowOpen,
} from 'shared-components';
import { RootState } from '@store/reducers';
import { CloseWindowButtonStyled } from '@components/_shared/AppBar/CloseWindowButtonStyled';
import { CloseIframeButtonStyled } from '@components/_shared/AppBar/CloseIframeButtonStyled';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsListProps,
  TabsProps,
  TabsTrigger,
  TabsTriggerProps,
} from '@radix-ui/react-tabs';
import { EQLeadNotReady } from '@components/EQLeadFlow/Step0/EQLeadNotReady';

const InfoBoxCard = ({ title, description }: { title: string; description: string }) => {
  return (
    <div className="grid grid-cols-1 gap-[10px] p-[24px] rounded-[5px] border border-spark bg-[linear-gradient(180deg,#FFF_10%,#D5D973_80%)]">
      <Text variant="body-general" className="!text-[26px] !font-extended text-center !leading-[110%]">
        {title}
      </Text>
      <Text variant="body-general" className="!text-[14px] !font-standard text-center !leading-[110%]">
        {description}
      </Text>
    </div>
  );
};

const WhyTentoPoint = ({ title, bullets }: { title: string; bullets: string[] }) => {
  return (
    <div className="px-[20px] pt-[20px] pb-[40px] grid grid-cols-[1fr] w479:grid-cols-[40px_1fr_1fr] gap-[20px]  border-b border-b-dawn2">
      <div className="grid items-center">
        <Button
          type="button"
          aria-hidden
          className="rounded-[9999px] relative bg-spark border-spark2 hover:bg-spark2 w-[40px] h-[40px] p-0 m-0 flex items-center justify-center"
          onClick={() => {}}
          disabled
        >
          <ArrowRight width="8" height="13" className="absolute top-[13px]" />
        </Button>
      </div>
      <div className="grid items-center">
        <Text variant="body-general" className="!text-[22px] !font-extended !leading-[120%]">
          {title}
        </Text>
      </div>
      <div className="pt-[20px]">
        {bullets.map((bullet, index) => (
          <div key={index} className="flex items-start space-x-2 mb-[10px]">
            • {bullet}
          </div>
        ))}
      </div>
    </div>
  );
};

const WhyChooseTentoStep = ({
  buttonText,
  title,
  description,
}: {
  buttonText: string;
  title: string;
  description: string;
}) => {
  return (
    <div className="px-[40px] pt-[0px] pb-[20px] grid grid-cols-1 gap-[10px] border-b border-b-dawn2">
      <div className="flex justify-center mx-auto">
        <Button
          type="button"
          className="rounded-[100px] relative bg-spark border-spark2 hover:bg-spark2 h-[34px] px-[15px] py-[7.5px] m-0 flex items-center justify-center"
          onClick={() => {}}
          disabled
        >
          {buttonText}
        </Button>
      </div>
      <div className="grid items-center justify-center mx-auto">
        <div className="grid items-center pt-[10px] align-center max-w-[672px] text-center">
          <Text variant="body-general" className="!text-[22px] !font-standard text-center !leading-[110%]">
            {title}
          </Text>
        </div>
      </div>
      <div className="grid items-center justify-center mx-auto">
        <div className="max-w-[448px] mx-auto align-center text-center">
          <Text variant="body-general" className="!text-[16px] !font-standard text-center !leading-[120%]">
            {description}
          </Text>
        </div>
      </div>
    </div>
  );
};

const TestimonialsCarousel = () => {
  const testimonials = [
    {
      quote:
        'The rates were better than what my bank offered, and Sarah walked me through everything. Finally found a lender that actually cares about small businesses.',
      author: '— JENNIFER, RODRIGUEZ LANDSCAPING',
    },
    {
      quote:
        'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
      author: '— Mateusz, RODRIGUEZ LANDSCAPING',
    },
    {
      quote:
        'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
      author: '— Vic, RODRIGUEZ LANDSCAPING',
    },
  ];
  const slider = {
    variant: 'default',
    autoplay: true,
    tabsProps: {} as Partial<TabsProps>,
    tabsListProps: {} as Partial<TabsListProps>,
    tabsTriggerProps: {} as Partial<TabsTriggerProps>,
    tabsTriggerDivProps: {} as Partial<React.HTMLAttributes<HTMLDivElement>>,
    childrenArray: testimonials,
  };
  const breapoints = useResponsiveBreakpoints();
  const [selectedTab, setSelectedTab] = useState('0');
  const sliderVariant = slider?.variant ?? 'default';
  const sliderAutoplay = slider?.autoplay ?? slider ? true : false;
  const selectedTestimonial = testimonials[parseInt(selectedTab, 10)] || testimonials[0];

  return (
    <Tabs value={selectedTab} defaultValue={selectedTab} {...(slider.tabsProps ?? {})}>
      <div className="grid grid-cols-1 gap-[40px]">
        <TabsList
          {...(slider.tabsListProps ?? {})}
          className={cn(' h-[17px] rounded-full bg-transparent', slider.tabsListProps?.className ?? '')}
        >
          {slider?.childrenArray.map((child, index) => (
            <TabsTrigger value={index?.toString() ?? ''} {...(slider.tabsTriggerProps ?? {})}>
              <div
                {...(slider.tabsTriggerDivProps ?? {})}
                className={cn('mr-[5px]', slider.tabsTriggerDivProps?.className ?? '')}
                onClick={(e) => {
                  setSelectedTab(index?.toString() ?? '');
                  if (slider.tabsTriggerDivProps?.onClick) {
                    slider.tabsTriggerDivProps.onClick(e);
                  }
                }}
              >
                <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect
                    x="0.5"
                    y="0.5"
                    width="14"
                    height="14"
                    rx="7"
                    fill={selectedTab === index?.toString() ? '#D5D973' : 'transparent'}
                    stroke="#0D0D0D"
                  />
                </svg>
              </div>
            </TabsTrigger>
          ))}
        </TabsList>
        <div className="grid grid-cols-1 gap-[40px]">
          <div>
            <Text variant="body-general" className="!text-[32px] !font-extended !leading-[120%]">
              {selectedTestimonial.quote}
            </Text>
          </div>
          <div>
            <Text variant="body-general" className="!text-[16px] !font-condensed !leading-[120%]">
              {selectedTestimonial.author}
            </Text>
          </div>
        </div>
      </div>
    </Tabs>
  );
};

export const EmbedLearnMoreConnected = ({ vendor_url, logo }: { vendor_url: string; logo: string }) => {
  const responsiveBreakpoints = useResponsiveBreakpoints();
  const isOpenedInWindow = useWasOpenedByWindowOpen();
  const isOpenedInIframe = useIsInIframe();

  return (
    <div className="">
      <header
        className={`sticky top-0 z-[2000] bg-white learnmore-bar border-b-[1px] border-b-charcoal3 shadow-[0_4px_10px_0_rgba(0,0,0,0.05)]`}
      >
        <div className="nav-background">
          <div
            className={`appHeader MuiToolbar-root MuiToolbar-regular MuiToolbar-gutters shadow-none justify-between !grid ${
              responsiveBreakpoints.isMobile ? 'grid-cols-2' : 'grid-cols-3'
            }`}
          >
            <div>
              <a href={'https://www.tento.com/'} className={'logo-wrap'}>
                <svg
                  width="60"
                  height="60"
                  viewBox="0 0 60 60"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  xmlnsXlink="http://www.w3.org/1999/xlink"
                >
                  <path
                    d="M0 5C0 2.23858 2.23858 0 5 0H55C57.7614 0 60 2.23858 60 5V55C60 57.7614 57.7614 60 55 60H5C2.23858 60 0 57.7614 0 55V5Z"
                    fill="url(#pattern0_6035_1598)"
                  />
                  <defs>
                    <pattern id="pattern0_6035_1598" patternContentUnits="objectBoundingBox" width="1" height="1">
                      <use xlinkHref="#image0_6035_1598" transform="scale(0.00119048)" />
                    </pattern>
                    <image
                      id="image0_6035_1598"
                      width="840"
                      height="840"
                      preserveAspectRatio="none"
                      xlinkHref="data:image/png;base64,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"
                    />
                  </defs>
                </svg>

                {/* <img src={logo} alt="" className={`transition-all duration-500 ease-in-out logo h-[28px]`} /> */}
              </a>
            </div>
            {!responsiveBreakpoints.isMobile && (
              <div className="grid align-center justify-center">
                <Text variant="body-general" className="!text-[16px] !font-extended uppercase text-center">
                  Finance with Tento
                </Text>
              </div>
            )}
            <div className="flex items-center justify-end gap-[10px]">
              {isOpenedInWindow || isOpenedInIframe ? (
                <>
                  <CloseWindowButtonStyled
                    version={'black'}
                    xProps={{
                      width: '28',
                      height: '28',
                    }}
                  />
                  <CloseIframeButtonStyled
                    version={'black'}
                    xProps={{
                      width: '28',
                      height: '28',
                    }}
                  />
                </>
              ) : null}
            </div>
          </div>
        </div>
      </header>
      <div className="p-[40px] grid gap-[40px]">
        <div className="grid gap-[20px]">
          <Text
            variant="body-general"
            className="!text-[32px] max-w-[447px] mx-auto !font-extended text-center !leading-[110%]"
          >
            Equipment Financing That Works on Your Timeline.
          </Text>
          <Text
            variant="body-general"
            className="!text-[16px] max-w-[752px] mx-auto !font-extended uppercase text-center !leading-[110%]"
          >
            Skip the runaround. Get fast, fair financing today.
          </Text>
          <div className="grid gap-[10px] grid-cols-1 w479:grid-cols-3 pt-[20px] pb-[10px]">
            <InfoBoxCard title="$500M+" description="Equipment Financed" />
            <InfoBoxCard title="2 Hours" description="Average Decision Time" />
            <InfoBoxCard title="4.9 out of 5" description="Customer Rating" />
          </div>
        </div>
        <div className="grid gap-[10px]">
          <Text
            variant="body-general"
            className="!text-[32px] max-w-[447px] mx-auto !font-extended text-center !leading-[110%] pb-[15px]"
          >
            Why Choose Tento?
          </Text>
          <WhyTentoPoint
            title="Lightning-Fast Decisions"
            bullets={['Pre-qualification in under 3 minutes', 'Same-day approvals available']}
          />
          <WhyTentoPoint
            title="Most Competitive Rates"
            bullets={['The best offer designed for your needs', 'Terms from 12 to 84 months']}
          />
          <WhyTentoPoint
            title="Concierge-Style Support"
            bullets={['Single point of contact throughout', 'Your information stays private']}
          />
        </div>
      </div>
      <div className="py-[60px] px-[60px] bg-[linear-gradient(180deg,#FFF_0%,#D5D973_60%,#FFF_100%)]">
        <TestimonialsCarousel />
      </div>
      <div className="grid gap-[40px]">
        <Text
          variant="body-general"
          className="!text-[32px] max-w-[447px] mx-auto !font-extended text-center !leading-[110%] pb-[15px]"
        >
          How Does It Work?
        </Text>
        <WhyChooseTentoStep
          buttonText="Step 01"
          title="Select Finance with Tento"
          description={`On the equipment you are looking to acquire, click the "Finance with Tento" button and start our ~3 minute application.`}
        />
        <WhyChooseTentoStep
          buttonText="Step 02"
          title="Pre–Qualify in Minutes"
          description={`Pre-qualify in 3 steps and less than 3 minutes.`}
        />
        <WhyChooseTentoStep
          buttonText="Step 03"
          title="Get Funded in Hours"
          description={`Secure your funds in hours not days, and get your equipment to keep your business running.`}
        />
      </div>
      <div className="grid gap-[20px] px-[40px] pb-[60px] pt-[20px]">
        <Text variant="body-general" className="!text-[32px] mx-auto !font-extended text-center !leading-[110%]">
          Not Ready to Apply?
        </Text>
        <Text
          variant="body-general"
          className="!text-[16px] max-w-[752px] mx-auto !font-extended uppercase text-center !leading-[110%] pb-[20px]"
        >
          We'll text you a link, so you can get started when you're ready
        </Text>
        <EQLeadNotReady />
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    theme: state.theme,
    logo: state.brand.data.media.logo_url,
    vendor_url: state.brand?.data?.vendor_url,
    formValues: state.form?.application?.values ?? {},
  };
};

export const EmbedLearnMore = connect(mapStateToProps)(EmbedLearnMoreConnected);
